import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { BehaviorSubject, debounceTime, merge } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { filterBoolean, trackByProp } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import { DialogRef, DropdownWidth } from '@core/user-interface-legacy';

import { SelectLocationsDialogDataService } from './select-locations-dialog-data.service';
import { SelectLocationsDialogGroupsService } from './select-locations-dialog-groups.service';
import { selectLocationsDialogImports } from './select-locations-dialog.imports';
import type {
  DialogData,
  TemplateGroup,
  SortOptionsType,
  LocationItem,
} from './select-locations-dialog.types';
import { SelectMode, SortOption, TemplateKind } from './select-locations-dialog.types';

@Component({
  selector: 'rt-select-locations-dialog',
  templateUrl: './select-locations-dialog.component.html',
  styleUrls: ['./select-locations-dialog.component.less'],
  imports: [...selectLocationsDialogImports],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [SelectLocationsDialogDataService, SelectLocationsDialogGroupsService],
})
export class SelectLocationsDialogComponent implements DialogComponentI<DialogData, number[]> {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(DialogRef);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly dataService = inject(SelectLocationsDialogDataService);
  private readonly groupsService = inject(SelectLocationsDialogGroupsService);

  private readonly destroyRef = inject(DestroyRef);
  public locationGroups: TemplateGroup[] = [];
  public locationsLoaded: boolean = false;
  public queryControl = new FormControl('', { nonNullable: true });
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public sortDropdownOpened = false;
  public assignedLocationIds: number[];
  public selectMode: SelectMode;
  public locationSlotsNumber?: number;
  public hasAddNewLocation?: boolean;
  public hasGoToSettings?: boolean;
  public availableLocationIds?: number[];
  public currentSort$: Observable<SortOption>;
  public heading = this.i18n.translate('app.locations.assign_locations_dialog.heading');
  public subheading = this.i18n.translate('app.locations.assign_locations_dialog.subheading');
  public submitLabel: string = this.i18n.translate('app.common.confirm');
  public goBackHandler?: () => void;
  public hideInvalidLocations: boolean;
  public isLoading: boolean;
  public hasSkip: boolean;
  public companyId?: number;
  public TemplateKind = TemplateKind;
  public DropdownWidth = DropdownWidth;

  public TooltipLabelTranslations = {
    canNotAssignArchivedLocation: this.i18n.translate(
      'app.locations.assign_locations_dialog.tooltips.can_not_assign_archived_location',
    ),
    canNotAssignRemovedLocation: this.i18n.translate(
      'app.locations.assign_locations_dialog.tooltips.can_not_assign_removed_location',
    ),
    canNotAssignInvalidLocation: this.i18n.translate(
      'app.locations.assign_locations_dialog.tooltips.can_not_assign_invalid_location',
    ),
    canNotAssignLocation: () =>
      this.i18n.translate(
        'app.locations.assign_locations_dialog.tooltips.can_not_assign_location',
        { count: this.locationSlotsNumber },
      ),
  };

  private addNewLocationHandler$?: (assignedLocationIds: number[]) => void;
  private initialAssignedLocationIds: number[] = [];
  private allLocations: LocationItem[] = [];
  private _currentSort$: BehaviorSubject<SortOption> = new BehaviorSubject(SortOption.Name);
  private saveHandler$?: (assignedIds: number[]) => Observable<boolean>;
  private isAlwaysSaveable: boolean;

  constructor() {
    this.currentSort$ = this._currentSort$.asObservable();
  }

  public get isAllOptionAvailable() {
    if (this.locationSlotsNumber === undefined) return true;
    return this.allLocations.length <= this.locationSlotsNumber;
  }

  public get isMultiSelectMode() {
    return this.selectMode === SelectMode.Multi;
  }

  public get isSingleSelectMode() {
    return this.selectMode === SelectMode.Single;
  }

  public get sortLabelTranslations() {
    return this.groupsService.sortLabelTranslations;
  }

  private get isAvailableLocationSlots() {
    if (this.locationSlotsNumber === undefined) return true;
    return !!(this.locationSlotsNumber - this.assignedLocationIds.length);
  }

  public ngOnInit() {
    const locationList$ = this.availableLocationIds
      ? this.dataService.locationsListByIds$(
          this.availableLocationIds,
          this.assignedLocationIds,
          this.hideInvalidLocations,
          this.companyId,
        )
      : this.dataService.allLocationsList$(this.assignedLocationIds, this.hideInvalidLocations);

    locationList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locationsList: LocationItem[]) => {
        this.allLocations = locationsList;
        this.locationsLoaded = true;
        this.setLocationGroups();
      });

    merge(
      this.queryControl.valueChanges.pipe(
        debounceTime(100),
        map(query => query.toLowerCase()),
      ),
      this.currentSort$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setLocationGroups();
      });
  }

  public setDialogData(data: DialogData) {
    if (data.heading) this.heading = data.heading;
    if (data.subheading) this.subheading = data.subheading;
    if (data.submitLabel) this.submitLabel = data.submitLabel;
    this.availableLocationIds = data.availableLocationIds;
    this.initialAssignedLocationIds = data.initialAssignedLocationIds;
    this.assignedLocationIds = data.assignedLocationIds ?? this.initialAssignedLocationIds;
    this.selectMode = data.selectMode;
    this.locationSlotsNumber = data.locationSlotsNumber;
    this.hasAddNewLocation = data.hasAddNewLocation;
    this.hasGoToSettings = data.hasGoToSettings;
    this.hasSkip = !!data.hasSkip;
    this.isAlwaysSaveable = !!data.isAlwaysSaveable;
    this.companyId = data?.companyId;
    this.hideInvalidLocations = !!data.hideInvalidLocations;
    this.addNewLocationHandler$ = data.addNewLocationHandler$;
    this.goBackHandler = data.goBackHandler;
    this.saveHandler$ = data.saveHandler$;
  }

  public selectItem(location: LocationItem) {
    const { isArchived, isRemoved, id, isValid } = location;
    const isSelected = this.assignedLocationIds.includes(id);

    if ((isRemoved || isArchived || (!isValid && this.hideInvalidLocations)) && !isSelected) {
      return;
    }

    if (isSelected) {
      this.assignedLocationIds = this.assignedLocationIds.filter(locationId => locationId !== id);
      this.cdRef.detectChanges();
      return;
    }

    if (this.isMultiSelectMode && !this.isAvailableLocationSlots) {
      return;
    }

    switch (this.selectMode) {
      case SelectMode.Single:
        this.assignedLocationIds = [id];
        break;
      case SelectMode.Multi:
        this.assignedLocationIds = [...this.assignedLocationIds, id];
        break;
      default:
        break;
    }
    this.cdRef.detectChanges();
  }

  public save() {
    if (this.saveHandler$) {
      this.isLoading = true;
      this.cdRef.detectChanges();
      this.saveHandler$(this.assignedLocationIds)
        .pipe(take(1), filterBoolean)
        .subscribe(() => {
          this.isLoading = false;
          this.dialogRef.close(this.assignedLocationIds);
        });
    } else {
      this.dialogRef.close(this.assignedLocationIds);
    }
  }

  public goBack() {
    this.goBackHandler && this.goBackHandler();
    this.dialogRef.close();
  }

  public addNewLocation() {
    if (this.addNewLocationHandler$) {
      this.addNewLocationHandler$(this.assignedLocationIds);
      this.dialogRef.close();
    }
  }

  public cancel() {
    this.dialogRef.close();
  }

  public skip() {
    this.saveHandler$ &&
      this.saveHandler$([...this.initialAssignedLocationIds])
        .pipe(take(1))
        .subscribe();
    this.dialogRef.close([...this.initialAssignedLocationIds]);
  }

  public isSelected(location: LocationItem): boolean {
    return this.assignedLocationIds.includes(location.id);
  }

  public isConfirmDisabled(): boolean {
    return (
      !this.isAlwaysSaveable &&
      JSON.stringify([...this.initialAssignedLocationIds].sort()) ===
        JSON.stringify([...this.assignedLocationIds].sort())
    );
  }

  public toggleSortDropdown() {
    this.sortDropdownOpened = !this.sortDropdownOpened;
    this.cdRef.detectChanges();
  }

  public sortOptionsToArray(): SortOption[] {
    return Object.values(SortOption);
  }

  public trackByValue<V>(index: number, value: V): V {
    return value;
  }

  public trackByKey(index: number, templateGroup: TemplateGroup) {
    return templateGroup.key;
  }

  public changeSortOption(sortOption: SortOptionsType): void {
    this._currentSort$.next(sortOption);
  }

  public selectAll() {
    this.assignedLocationIds = this.allLocations.map(location => location.id);
    this.cdRef.detectChanges();
  }

  public selectNone() {
    this.assignedLocationIds = [];
    this.cdRef.detectChanges();
  }

  public goToLocationsSettings() {
    this.router.navigateByUrl('/settings/locations');
    this.cancel();
  }

  public isLocationItemDisabled(location: LocationItem) {
    return (
      !this.isSelected(location) &&
      (!this.isAvailableLocationSlots ||
        location.isArchived ||
        location.isRemoved ||
        (!location.isValid && this.hideInvalidLocations))
    );
  }

  public getLocationItemTooltip(location: LocationItem) {
    if (location.isRemoved) {
      return this.TooltipLabelTranslations.canNotAssignRemovedLocation;
    }
    if (location.isArchived) {
      return this.TooltipLabelTranslations.canNotAssignArchivedLocation;
    }
    if (!location.isValid && this.hideInvalidLocations) {
      return this.TooltipLabelTranslations.canNotAssignInvalidLocation;
    }

    return this.TooltipLabelTranslations.canNotAssignLocation();
  }

  private setLocationGroups() {
    const filteredLocations = this.groupsService.filterLocations(
      this.allLocations,
      this.queryControl.value,
    );
    const locationGroups = this.groupsService.groupLocations(
      filteredLocations,
      this._currentSort$.value,
    );

    this.locationGroups = this.groupsService.sortByLength(locationGroups);
    this.cdRef.detectChanges();
  }
}
